import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useStore } from 'react-redux';
import { Mutex } from 'async-mutex';
import AppRoutes from 'constants/AppRoutes';
import { BAD_REQUEST } from 'constants/statusCodes';
import { io, type Socket } from 'socket.io-client';
import { authTokensSelector } from 'store/slices/auth/selectors';
import { logoutUser, setAuthState } from 'store/slices/auth/slice';
import { refNavigateTo } from 'utils/navigationRef';

import { useAppDispatch, useAppSelector } from '../store/hooks/useApp';
import isSocketConnectHttpError from './utils/isSocketConnectHttpError';

import type IRefreshTokenResponse from '../store/slices/auth/interfaces/IRefreshTokenResponse';
import type IBaseQueryResponse from '../store/types/IBaseQueryResponse';
import type { RootState } from '../store/types/TStore';
import type { IVCAgentSessionUnitUpdatedPayload } from './interfaces/IUseWebSocket';
import type IUseWebSocketOptions from './interfaces/IUseWebSocket';

const wsAuthRefreshMutex = new Mutex();

const useWebSocket = ({
  event,
  path = '',
  transports,
  shouldConnect = true,
  onMessage,
  onOpen,
  onClose,
  onError,
}: IUseWebSocketOptions = {}) => {
  const socketRef = useRef<Socket | null>(null);
  const socketUrl = process.env.REACT_APP_API_WS;
  const { accessToken } = useAppSelector(authTokensSelector);
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const [isConnected, setIsConnected] = useState(false);
  const [isSocketStopped, setIsSocketStopped] = useState(false);
  const refreshOncePerConnectionRef = useRef(false);
  const transportsKey = useMemo(() => transports?.join(','), [transports]);
  const normalizedTransports = useMemo(
    () => (transportsKey ? transportsKey.split(',') : undefined),
    [transportsKey],
  );

  const sendEvent = useCallback(<TPayload>(eventName: string, data: TPayload) => {
    const socket = socketRef.current;
    if (!socket?.connected) {
      return false;
    }

    socket.emit(eventName, data);
    return true;
  }, []);

  const sendVCAgentSessionUnitUpdated = useCallback((payload: IVCAgentSessionUnitUpdatedPayload) => {
    if (!event) {
      return false;
    }

    return sendEvent(event, payload);
  }, [event, sendEvent]);

  const destroySocket = useCallback((socket: Socket) => {
    socket.removeAllListeners();
    socket.close();

    if (socketRef.current === socket) {
      socketRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!shouldConnect) {
      setIsSocketStopped(false);
      return;
    }

    setIsSocketStopped(false);
  }, [accessToken, shouldConnect]);

  useEffect(() => {
    if (!shouldConnect) {
      return undefined;
    }

    if (isSocketStopped) {
      return undefined;
    }

    if (!socketUrl) {
      return undefined;
    }

    refreshOncePerConnectionRef.current = false;

    const socket = io(socketUrl, {
      autoConnect: false,
      reconnection: false,
      ...(normalizedTransports?.length ? { transports: normalizedTransports } : {}),
      ...(accessToken ? { auth: { token: accessToken } } : {}),
    });
    socketRef.current = socket;

    const handleConnect = () => {
      setIsConnected(true);
      onOpen?.();
    };

    const handleDisconnect = () => {
      setIsConnected(false);
      onClose?.(new CloseEvent('close'));
    };

    const refreshAccessTokenFromWsError = async (): Promise<string | null> => {
      const { refreshToken, userRefreshToken } = store.getState().auth;
      const generalRefreshToken = userRefreshToken || refreshToken;

      if (!generalRefreshToken) {
        dispatch(logoutUser());
        refNavigateTo(AppRoutes.login);
        return null;
      }

      const baseUrl = process.env.REACT_APP_API?.replace(/\/$/, '');
      if (!baseUrl) {
        return null;
      }

      let response: Response;
      try {
        response = await fetch(`${baseUrl}/auth/refresh/`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: generalRefreshToken }),
        });
      } catch {
        dispatch(logoutUser());
        refNavigateTo(AppRoutes.login);
        return null;
      }

      if (!response.ok) {
        dispatch(logoutUser());
        refNavigateTo(AppRoutes.login);
        return null;
      }

      let json: IBaseQueryResponse<IRefreshTokenResponse>;
      try {
        json = (await response.json()) as IBaseQueryResponse<IRefreshTokenResponse>;
      } catch {
        dispatch(logoutUser());
        refNavigateTo(AppRoutes.login);
        return null;
      }

      const { data } = json || {};

      if (!data) {
        dispatch(logoutUser());
        refNavigateTo(AppRoutes.login);
        return null;
      }

      const nextAccessToken = data.userAccessToken || data.accessToken;
      dispatch(setAuthState({
        accessToken: nextAccessToken,
      }));

      return nextAccessToken || null;
    };

    const handleConnectError = (connectError: unknown) => {
      const is400 = isSocketConnectHttpError(connectError, BAD_REQUEST);

      if (!is400) {
        const message = connectError instanceof Error
          ? connectError.message
          : String(connectError);
        onError?.(new ErrorEvent('error', { message }));
        return;
      }

      onError?.(new ErrorEvent('error', {
        message: `WebSocket connection failed with status ${BAD_REQUEST}`,
      }));

      const refreshOn400 = async () => {
        await wsAuthRefreshMutex.waitForUnlock();
        const release = await wsAuthRefreshMutex.acquire();
        try {
          if (refreshOncePerConnectionRef.current) {
            return;
          }
          refreshOncePerConnectionRef.current = true;

          if (socketRef.current === socket) {
            destroySocket(socket);
          }
          await refreshAccessTokenFromWsError();
          setIsSocketStopped(true);
        } finally {
          release();
        }
      };
      refreshOn400().catch(() => {});
    };

    const handleSocketMessage = (data: unknown) => {
      const dataStr = typeof data === 'string' ? data : JSON.stringify(data);
      onMessage?.(new MessageEvent('message', { data: dataStr }));
    };

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('connect_error', handleConnectError);

    if (onMessage && event) {
      socket.on(event, handleSocketMessage);
    } else if (onMessage) {
      socket.onAny((_eventName, ...args) => {
        const payload = args.length === 1 ? args[0] : args;
        handleSocketMessage(payload);
      });
    }

    socket.connect();

    return () => {
      setIsConnected(false);
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('connect_error', handleConnectError);

      if (onMessage && event) {
        socket.off(event, handleSocketMessage);
      } else if (onMessage) {
        socket.offAny();
      }

      destroySocket(socket);
    };
  }, [
    accessToken,
    destroySocket,
    dispatch,
    event,
    onClose,
    onError,
    onMessage,
    onOpen,
    path,
    normalizedTransports,
    isSocketStopped,
    shouldConnect,
    socketUrl,
    store,
  ]);

  return {
    isConnected,
    sendEvent,
    sendVCAgentSessionUnitUpdated,
    socket: socketRef.current,
  };
};

export default useWebSocket;
